import {
  MiniCalendarInternal,
  Props,
} from '@lmt/unicorn-shared/app/components/calendar/mini-calendar/MiniCalendar';
import { addMonths, differenceInMonths, startOfMonth } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';

const getStartOfFirstMonth = (selectedDate: Date) => {
  const selectedDateMonthStart = startOfMonth(selectedDate);
  const todayMonth = startOfMonth(new Date());
  const monthDiff = differenceInMonths(selectedDateMonthStart, todayMonth);

  return addMonths(todayMonth, Math.floor(monthDiff / 3) * 3);
};

/*
  In ThreeMonthMiniCalendar follows the rules:
    1. Months configuration is always one of the following:
        - Dec - Jan - Feb
        - Mar - Apr - May
        - Jun - Jul - Aug
        - Sep - Oct - Nov
    2. We scroll left or right always by 3 months
    3. If props.selectedDate is passed we scroll to the left or to the right (by 3 months) so that month of selectedDate is visible.

    E.g. if today=2020-03-07 and selectedDate=2020-02-01 we will show Dec-Jan-Feb in ThreeMonthCalendar
    (because we 'scrolled' one time to the left starting from March-April-May)
 */
export const ThreeMonthMiniCalendar: React.FC<
  Omit<Props, 'hideMonthArrows' | 'currentMonthDate' | 'onMonthChange'>
> = React.memo(function ThreeMonthMiniCalendar(props) {
  const [startOfFirstMonth, setStartOfFirstMonth] = useState(
    getStartOfFirstMonth(props.selectedDate),
  );

  const handleMonthChange = (newDate: Date) => {
    if (newDate < startOfFirstMonth) {
      setStartOfFirstMonth(addMonths(newDate, -2));
    } else if (newDate > startOfFirstMonth) {
      setStartOfFirstMonth(addMonths(newDate, 2));
    }
  };
  const secondMonthDate = useMemo(
    () => addMonths(startOfFirstMonth, 1),
    [startOfFirstMonth],
  );
  const thirdMonthDate = useMemo(
    () => addMonths(startOfFirstMonth, 2),
    [startOfFirstMonth],
  );

  useEffect(
    function onSelectedDateChanged() {
      // update date in MiniCalendar according to selected month in Calendar
      const monthDiff = differenceInMonths(
        startOfMonth(props.selectedDate),
        startOfFirstMonth,
      );

      if (monthDiff < 0 || monthDiff >= 3) {
        setStartOfFirstMonth(getStartOfFirstMonth(props.selectedDate));
      }
    },
    [props.selectedDate],
  );
  return (
    <div data-test-id={'three-months-mini-calendar'}>
      <MiniCalendarInternal
        {...props}
        currentMonthDate={startOfFirstMonth}
        onMonthChange={handleMonthChange}
        hideMonthArrows={false}
        disableMonthSwitchingByClickingOnDates={true}
      />
      <MiniCalendarInternal
        {...props}
        hideMonthArrows={true}
        currentMonthDate={secondMonthDate}
        disableMonthSwitchingByClickingOnDates={true}
      />
      <MiniCalendarInternal
        {...props}
        hideMonthArrows={true}
        currentMonthDate={thirdMonthDate}
        disableMonthSwitchingByClickingOnDates={true}
      />
    </div>
  );
});
